import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getGraphQLClient,
  GET_SCENE_EDITOR,
  UPDATE_SCENE_BODY,
  GENERATE_SCENE_PLAN_POINT_SUGGESTIONS,
  SAVE_SCENE_PLAN_POINT,
  DELETE_SCENE_PLAN_POINT,
  LOCK_SCENE_PLAN_POINT,
  UNLOCK_SCENE_PLAN_POINT,
  START_SCENE_INTERVIEW,
  SEND_SCENE_INTERVIEW_MESSAGE,
  CLEAR_SCENE_INTERVIEW,
  SWITCH_SCENE_INTERVIEW_ANGLE,
  UPDATE_WRITING_STYLE,
  GENERATE_WRITING_STYLE,
  GENERATE_SCENE_PREVIEW,
  EXPAND_DRAFT_BLOCK,
  CHECK_SCENE_FACTS,
  SAVE_BOOK_FACT,
} from '../lib/graphql';
import { captureException } from '../lib/sentry';
import { Scene, FactFinding } from '../types';
import type { TaggedSuggestion } from '../components/writing/ClaimSuggestionPanel';

type SceneEditorResponse = { scene: Scene | null };
type ScenePayload = { success: boolean; errors: string[]; scene: Scene | null };
type GenericPayload = { success: boolean; errors: string[] };
type AnyResponse = Record<string, ScenePayload | GenericPayload | undefined>;

interface Options {
  onError?: (message: string) => void;
  onNotice?: (message: string) => void;
}

const firstError = (errors: string[] | undefined, fallback: string) =>
  errors && errors.length > 0 ? errors[0] : fallback;

// Shared scene-editing logic (load + all mutations) used by the compact
// SceneToolsSheet in the book reader and available to the full scene editor.
// Drafts and per-control busy state stay in the consuming component; this hook
// owns the data and the network calls, reloading the scene after each change.
export function useSceneTools(
  sceneId: string | undefined,
  bookId: string | undefined,
  opts: Options = {}
) {
  const { onError, onNotice } = opts;
  const [scene, setScene] = useState<Scene | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!sceneId) {
      setLoading(false);
      return;
    }
    try {
      const data = await getGraphQLClient().request<SceneEditorResponse>(GET_SCENE_EDITOR, {
        sceneId,
      });
      setScene(data.scene);
    } catch (err) {
      captureException(err);
      onError?.(err instanceof Error ? err.message : 'Failed to load scene.');
    } finally {
      setLoading(false);
    }
  }, [sceneId, onError]);

  useEffect(() => {
    setLoading(true);
    void reload();
  }, [reload]);

  // Runs a mutation, surfaces the first error, and returns the typed payload (or
  // null on a network/transport error).
  const call = useCallback(
    async <K extends string>(
      mutation: string,
      variables: Record<string, unknown>,
      key: K
    ): Promise<ScenePayload | GenericPayload | null> => {
      try {
        const res = await getGraphQLClient().request<AnyResponse>(mutation, variables);
        return res[key] ?? null;
      } catch (err) {
        captureException(err);
        onError?.(err instanceof Error ? err.message : 'Something went wrong.');
        return null;
      }
    },
    [onError]
  );

  // Performs a mutation, reloads on success, and reports the first error/notice.
  const perform = useCallback(
    async (
      mutation: string,
      variables: Record<string, unknown>,
      key: string,
      fallbackError: string,
      notice?: string
    ): Promise<boolean> => {
      const payload = await call(mutation, variables, key);
      if (!payload?.success) {
        onError?.(firstError(payload?.errors, fallbackError));
        return false;
      }
      await reload();
      if (notice) onNotice?.(notice);
      return true;
    },
    [call, reload, onError, onNotice]
  );

  const activeInterview = useMemo(() => {
    const interviews = scene?.sceneInterviews ?? [];
    if (interviews.length === 0) return null;
    return interviews.find((iv) => iv.status === 'active') ?? interviews[0];
  }, [scene]);

  const draftBlockId = scene?.storyBiblePlotBeat?.draftBlock?.id ?? null;

  // ---- key moments ----
  const addPlanPoint = useCallback(
    (content: string) =>
      perform(
        SAVE_SCENE_PLAN_POINT,
        { sceneId, content: content.trim(), position: scene?.planPoints?.length ?? 0 },
        'saveScenePlanPoint',
        'Failed to save key moment.',
        'Key moment added.'
      ),
    [perform, sceneId, scene]
  );

  const savePlanPoint = useCallback(
    (id: string, content: string) =>
      perform(
        SAVE_SCENE_PLAN_POINT,
        { sceneId, planPointId: id, content: content.trim() },
        'saveScenePlanPoint',
        'Failed to save key moment.',
        'Key moment saved.'
      ),
    [perform, sceneId]
  );

  const deletePlanPoint = useCallback(
    (id: string) =>
      perform(
        DELETE_SCENE_PLAN_POINT,
        { planPointId: id },
        'deleteScenePlanPoint',
        'Failed to delete key moment.',
        'Key moment deleted.'
      ),
    [perform]
  );

  const togglePlanPointLock = useCallback(
    (id: string, locked: boolean) =>
      perform(
        locked ? UNLOCK_SCENE_PLAN_POINT : LOCK_SCENE_PLAN_POINT,
        { planPointId: id },
        locked ? 'unlockScenePlanPoint' : 'lockScenePlanPoint',
        'Failed to update key moment lock.'
      ),
    [perform]
  );

  const suggestScenePlan = useCallback(
    async (params: { focusTag?: string; mode?: 'fresh' | 'continue' | 'mix' } = {}) => {
      if (!sceneId) return [] as TaggedSuggestion[];
      try {
        const res = await getGraphQLClient().request<{
          generateScenePlanPointSuggestions: { suggestions: { items: TaggedSuggestion[] } | null };
        }>(GENERATE_SCENE_PLAN_POINT_SUGGESTIONS, {
          sceneId,
          focusTag: params.focusTag ?? undefined,
          mode: params.mode ?? 'fresh',
        });
        return res.generateScenePlanPointSuggestions?.suggestions?.items ?? [];
      } catch (err) {
        captureException(err);
        onError?.(err instanceof Error ? err.message : 'Failed to suggest key moments.');
        return [] as TaggedSuggestion[];
      }
    },
    [sceneId, onError]
  );

  // ---- interview ----
  const startInterview = useCallback(
    () =>
      perform(
        START_SCENE_INTERVIEW,
        { sceneId },
        'startSceneInterview',
        'Failed to start interview.',
        'Interview started.'
      ),
    [perform, sceneId]
  );

  const sendInterviewMessage = useCallback(
    (content: string) => {
      if (!activeInterview) return Promise.resolve(false);
      return perform(
        SEND_SCENE_INTERVIEW_MESSAGE,
        { interviewId: activeInterview.id, content: content.trim() },
        'sendSceneInterviewMessage',
        'Failed to send message.'
      );
    },
    [perform, activeInterview]
  );

  const switchAngle = useCallback(
    async (planPointId: string) => {
      if (!activeInterview) return startInterview();
      return perform(
        SWITCH_SCENE_INTERVIEW_ANGLE,
        { interviewId: activeInterview.id, scenePlanPointId: planPointId },
        'switchSceneInterviewAngle',
        'Failed to switch angle.'
      );
    },
    [perform, activeInterview, startInterview]
  );

  const clearInterview = useCallback(() => {
    if (!activeInterview) return Promise.resolve(false);
    return perform(
      CLEAR_SCENE_INTERVIEW,
      { interviewId: activeInterview.id },
      'clearSceneInterview',
      'Failed to clear interview.',
      'Interview cleared.'
    );
  }, [perform, activeInterview]);

  // ---- writing style ----
  const saveWritingStyle = useCallback(
    (text: string) => {
      if (!draftBlockId) return Promise.resolve(false);
      return perform(
        UPDATE_WRITING_STYLE,
        { draftBlockId, writingStyle: text.trim(), regeneratePreview: true },
        'updateWritingStyle',
        'Failed to save writing style.',
        'Writing style saved.'
      );
    },
    [perform, draftBlockId]
  );

  const generateWritingStyle = useCallback(
    (suggestion?: string) => {
      if (!draftBlockId) return Promise.resolve(false);
      return perform(
        GENERATE_WRITING_STYLE,
        { draftBlockId, suggestion: suggestion?.trim() || undefined, autoRegeneratePreview: true },
        'generateWritingStyle',
        'Failed to generate writing style.',
        'Writing style generation started.'
      );
    },
    [perform, draftBlockId]
  );

  // ---- generation / body ----
  const generatePreview = useCallback(
    (force?: boolean) =>
      perform(
        GENERATE_SCENE_PREVIEW,
        { sceneId, force },
        'generateScenePreview',
        'Failed to generate preview.',
        'Preview generation started.'
      ),
    [perform, sceneId]
  );

  const expandBody = useCallback(async () => {
    if (!bookId || !draftBlockId || !scene) return false;
    // Full expansion needs a preview to expand from.
    if (!scene.previewContent) {
      const ok = await call(
        GENERATE_SCENE_PREVIEW,
        { sceneId, force: true },
        'generateScenePreview'
      );
      if (!ok?.success) {
        onError?.(firstError(ok?.errors, 'Failed to generate preview.'));
        return false;
      }
      await reload();
    }
    const payload = await call(
      EXPAND_DRAFT_BLOCK,
      { bookId, draftBlockId, force: true },
      'expandDraftBlock'
    );
    if (!payload?.success) {
      onError?.(firstError(payload?.errors, 'Failed to generate full scene text.'));
      return false;
    }
    onNotice?.('Full scene body generation started. It may take a few minutes.');
    return true;
  }, [bookId, draftBlockId, scene, sceneId, call, reload, onError, onNotice]);

  const saveBody = useCallback(
    (content: string) =>
      perform(
        UPDATE_SCENE_BODY,
        { sceneId, content },
        'updateSceneBody',
        'Failed to save scene body.',
        'Scene body saved.'
      ),
    [perform, sceneId]
  );

  // ---- fact check (flag-only; no persistence) ----
  // Returns the AI's findings for this scene's prose; the sheet renders them for
  // review. Never edits the prose.
  const checkFacts = useCallback(async () => {
    if (!sceneId) return [] as FactFinding[];
    try {
      const res = await getGraphQLClient().request<{
        checkSceneFacts: { findings: FactFinding[]; errors: string[] };
      }>(CHECK_SCENE_FACTS, { sceneId });
      const payload = res.checkSceneFacts;
      if (payload?.errors?.length) onError?.(payload.errors[0]);
      return payload?.findings ?? [];
    } catch (err) {
      captureException(err);
      onError?.(err instanceof Error ? err.message : 'Failed to check facts.');
      return [] as FactFinding[];
    }
  }, [sceneId, onError]);

  // Promote a flagged claim into a curated, verified BookFact.
  const addFact = useCallback(
    async (claim: string) => {
      if (!bookId) return false;
      try {
        const res = await getGraphQLClient().request<{
          saveBookFact: { bookFact: { id: string } | null; errors: string[] };
        }>(SAVE_BOOK_FACT, { bookId, claim: claim.trim(), status: 'verified' });
        const payload = res.saveBookFact;
        if (!payload?.bookFact) {
          onError?.(firstError(payload?.errors, 'Failed to add fact.'));
          return false;
        }
        onNotice?.('Added to facts.');
        return true;
      } catch (err) {
        captureException(err);
        onError?.(err instanceof Error ? err.message : 'Failed to add fact.');
        return false;
      }
    },
    [bookId, onError, onNotice]
  );

  return {
    scene,
    loading,
    reload,
    activeInterview,
    draftBlockId,
    addPlanPoint,
    savePlanPoint,
    deletePlanPoint,
    togglePlanPointLock,
    suggestScenePlan,
    startInterview,
    sendInterviewMessage,
    switchAngle,
    clearInterview,
    saveWritingStyle,
    generateWritingStyle,
    generatePreview,
    expandBody,
    saveBody,
    checkFacts,
    addFact,
  };
}
